import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ZonePicker } from '../../../components/climb-create/ZonePicker';
import type { Step2ZoneSummary } from '../../../components/climb-create/Step2Form';
import { RoutesetterPicker, type RoutesetterSelection } from '../../../components/routesetters/RoutesetterPicker';
import type { ClimbScan } from '../../../types/climb-scan';
import type { Zone } from '../../../types/zone';

/** Whether the scan has been told where it is and who set it. */
function isScanCredited(scan: ClimbScan): boolean {
  return !!scan.zone && scan.route_setters.length > 0;
}

interface ScanDefaultsProps {
  scan: ClimbScan;
  zones: Zone[];
  saving: boolean;
  onSave: (zone: Step2ZoneSummary | null, setters: RoutesetterSelection[]) => void;
}

/**
 * The scan's zone and the setters credited on it — every climb starts from
 * these, so they are answered once and then got out of the way.
 *
 * It folds itself shut on save: a scan whose zone and setters are settled has
 * nothing more to ask, and the panel below it is where the work actually
 * happens. It opens on a scan that has not answered them yet.
 */
export function ScanDefaults({ scan, zones, saving, onSave }: ScanDefaultsProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(() => (isScanCredited(scan) ? '' : 'settings'));
  const [zone, setZone] = useState<Step2ZoneSummary | null>(
    scan.zone ? { ...scan.zone, image_small_url: scan.zone.image_small_url } : null
  );
  const [setters, setSetters] = useState<RoutesetterSelection[]>(scan.route_setters);

  // Switching scans reuses this component, so its fields follow the new scan —
  // and so does whether it has anything left to ask.
  useEffect(() => {
    setZone(scan.zone ? { ...scan.zone, image_small_url: scan.zone.image_small_url } : null);
    setSetters(scan.route_setters);
    setOpen(isScanCredited(scan) ? '' : 'settings');
  }, [scan.id, scan.zone, scan.route_setters, scan]);

  return (
    <Accordion type="single" collapsible value={open} onValueChange={setOpen}>
      <AccordionItem value="settings" className="overflow-hidden rounded-md border">
        <AccordionTrigger className="px-3 py-2 text-xs font-medium hover:no-underline">
          {/* Shut, the trigger has to say what was answered, or folding it away
              would hide the one thing it was for. */}
          <span className="flex flex-wrap items-center gap-1 text-left">
            <span>{t('gymAdmin.scans.settings')}</span>
            {scan.zone && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                {scan.zone.name}
              </span>
            )}
            {scan.route_setters.map((setter) => (
              <span
                key={setter.id}
                className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground"
              >
                {setter.name}
              </span>
            ))}
          </span>
        </AccordionTrigger>

        <AccordionContent className="space-y-3 px-3 pb-3">
      {zones.length > 0 && (
        <div className="space-y-1">
          <Label>{t('gymAdmin.scans.zone')}</Label>
          <ZonePicker zones={zones} value={zone} onChange={setZone} />
        </div>
      )}

      <div className="space-y-1">
        <Label>{t('gymAdmin.scans.routeSetters')}</Label>
        <RoutesetterPicker
          mode="multiple"
          value={setters}
          onChange={setSetters}
          locationId={scan.location?.slug ?? null}
          placeholder={t('gymAdmin.scans.routeSetterPlaceholder')}
        />
      </div>

      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={saving}
        onClick={() => {
          // Shut straight away rather than on the save landing: the answer is
          // given, and a section that folds only once the server agrees reads
          // as if the button had not worked.
          setOpen('');
          onSave(zone, setters);
        }}
      >
        {saving && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
        {t('gymAdmin.scans.saveDefaults')}
      </Button>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
